import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import API from "../services/api";
import SeoHead from "../components/seo/SeoHead";
import Footer from "../components/Footer";
import useI18n from "../i18n/useI18n";

const THEME_KEY = "hookora_theme";

function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key || value === undefined || value === null ? fallback : value;
  };

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    referral_code: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [referralWarning, setReferralWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [theme, setTheme] = useState(getStoredTheme());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [activeField, setActiveField] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");

    if (ref && ref.trim()) {
      setFormData((prev) => ({
        ...prev,
        referral_code: ref.trim().toUpperCase(),
      }));
      setInfoMessage(
        tt(
          "registerPage.referralDetected",
          "Referral code detected and applied to your signup."
        )
      );
    }
  }, [location.search, t]);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(34,197,94,0.10), transparent 26%), radial-gradient(circle at top right, rgba(59,130,246,0.10), transparent 24%)"
        : "radial-gradient(circle at top left, rgba(34,197,94,0.08), transparent 24%), radial-gradient(circle at top right, rgba(59,130,246,0.08), transparent 22%)",
      cardBg: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.78)",
      border: isDark ? "rgba(148,163,184,0.16)" : "rgba(229,231,235,0.9)",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      divider: isDark ? "rgba(148,163,184,0.14)" : "#f1f5f9",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.95)",
      inputText: isDark ? "#f8fafc" : "#111827",
      accentLink: isDark ? "#93c5fd" : "#2563eb",
      badgeBg: isDark ? "rgba(5,150,105,0.18)" : "#ecfdf5",
      badgeText: isDark ? "#86efac" : "#047857",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      infoBg: isDark ? "rgba(8,47,73,0.28)" : "#ecfeff",
      infoBorder: isDark ? "rgba(34,211,238,0.30)" : "#a5f3fc",
      infoText: isDark ? "#a5f3fc" : "#155e75",
      warningBg: isDark ? "rgba(120,53,15,0.28)" : "#fff7ed",
      warningBorder: isDark ? "rgba(251,191,36,0.24)" : "#fdba74",
      warningText: isDark ? "#fde68a" : "#9a3412",
      hintBg: isDark ? "rgba(30,41,59,0.72)" : "#f8fafc",
      hintBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      hintGood: isDark ? "#86efac" : "#15803d",
      hintMuted: isDark ? "#94a3b8" : "#6b7280",
      eyeButtonBg: isDark ? "#1e293b" : "#f3f4f6",
      eyeButtonText: isDark ? "#e2e8f0" : "#374151",
      glowBlue: isDark ? "rgba(59,130,246,0.24)" : "rgba(59,130,246,0.18)",
      glowPurple: isDark ? "rgba(124,58,237,0.22)" : "rgba(124,58,237,0.16)",
      glowGreen: isDark ? "rgba(16,185,129,0.18)" : "rgba(16,185,129,0.14)",
      shadow: isDark
        ? "0 22px 60px rgba(2, 6, 23, 0.58)"
        : "0 20px 60px rgba(15, 23, 42, 0.12)",
      cardGlow: isDark
        ? "0 0 0 1px rgba(255,255,255,0.02), 0 24px 80px rgba(2,6,23,0.55)"
        : "0 0 0 1px rgba(255,255,255,0.6), 0 24px 70px rgba(15,23,42,0.10)",
      buttonShadow: loading
        ? "none"
        : isDark
        ? "0 16px 30px rgba(5,150,105,0.22)"
        : "0 14px 26px rgba(5,150,105,0.22)",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      inputFocusShadow: isDark
        ? "0 0 0 4px rgba(59,130,246,0.12)"
        : "0 0 0 4px rgba(59,130,246,0.10)",
    }),
    [isDark, loading]
  );

  const passwordChecks = useMemo(() => {
    const password = formData.password || "";
    return {
      minLength: password.length >= 6,
      hasLetter: /[A-Za-z]/.test(password),
      hasNumberOrSymbol: /[\d\W_]/.test(password),
    };
  }, [formData.password]);

  const inputStyle = (fieldName) => ({
    width: "100%",
    padding: "14px",
    borderRadius: "14px",
    border: `1px solid ${
      activeField === fieldName ? "#3b82f6" : colors.borderStrong
    }`,
    boxSizing: "border-box",
    fontSize: "15px",
    outline: "none",
    background: colors.inputBg,
    color: colors.inputText,
    transition: "all 180ms ease",
    boxShadow:
      activeField === fieldName ? colors.inputFocusShadow : "none",
    transform: activeField === fieldName ? "translateY(-1px)" : "translateY(0)",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "referral_code" ? value.toUpperCase() : value
    }));
  };

  const extractErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;

    if (typeof detail === "string") {
      return detail;
    }

    if (Array.isArray(detail)) {
      return detail.map((item) => item.msg).join(", ");
    }

    return tt("registerPage.messages.registrationFailed", "Registration failed.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setInfoMessage("");
      setReferralWarning("");

      const payload = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        referral_code: formData.referral_code.trim() || undefined,
      };

      const response = await API.post("/users/register", payload);
      const responseData = response?.data || {};

      setRegisteredEmail(payload.email);
      setSuccess(
        responseData?.message ||
          tt(
            "registerPage.messages.accountCreated",
            "Account created successfully. Please verify your email before logging in."
          )
      );

      if (responseData?.referral_warning) {
        setReferralWarning(responseData.referral_warning);
      }

      setFormData((prev) => ({
        ...prev,
        password: "",
      }));
      setShowPassword(false);
    } catch (err) {
      console.error("Registration error:", err?.response?.data || err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const emailToUse = registeredEmail || formData.email.trim();

    if (!emailToUse) {
      setError(
        tt(
          "registerPage.messages.enterEmailForResend",
          "Enter your email first so we can resend the verification link."
        )
      );
      return;
    }

    try {
      setResendLoading(true);
      setError("");
      setInfoMessage("");

      const response = await API.post("/users/resend-verification", {
        email: emailToUse,
      });

      setInfoMessage(
        response?.data?.message ||
          tt(
            "registerPage.messages.verificationSent",
            "Verification email sent successfully."
          )
      );
    } catch (err) {
      console.error("Resend verification error:", err?.response?.data || err);
      setError(
        err?.response?.data?.detail ||
          tt(
            "registerPage.messages.resendFailed",
            "Failed to resend verification email. Please try again."
          )
      );
    } finally {
      setResendLoading(false);
    }
  };

  const renderCheck = (passed, label) => (
    <div
      style={{
        color: passed ? colors.hintGood : colors.hintMuted,
        fontSize: "13px",
        lineHeight: "1.5",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <span style={{ fontWeight: "700" }}>{passed ? "✓" : "•"}</span>
      <span>{label}</span>
    </div>
  );

  const staggerStyle = (delay) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0px)" : "translateY(16px)",
    transition: `opacity 500ms ease ${delay}ms, transform 500ms ease ${delay}ms`,
  });

  return (
    <div
      style={{
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>
        {`
          @keyframes hookoraFloatOne {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(20px, -18px, 0) scale(1.06); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }

          @keyframes hookoraFloatTwo {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(-24px, 16px, 0) scale(1.04); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }

          @keyframes hookoraFloatThree {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(12px, 22px, 0) scale(1.08); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }

          @keyframes hookoraPulseGlow {
            0% { opacity: 0.75; }
            50% { opacity: 1; }
            100% { opacity: 0.75; }
          }

          @keyframes hookoraButtonShimmer {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(220%); }
          }
        `}
      </style>

      <SeoHead
        title={tt("registerPage.seoTitle", "Register | Hookora")}
        description={tt(
          "registerPage.seoDescription",
          "Create your Hookora account and start generating hooks, titles, captions, scripts, and more from one workflow."
        )}
        path="/register"
      />

      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-60px",
            left: "-40px",
            width: isMobile ? "220px" : "320px",
            height: isMobile ? "220px" : "320px",
            background: colors.glowBlue,
            filter: "blur(60px)",
            borderRadius: "999px",
            animation: "hookoraFloatOne 10s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "16%",
            right: "-80px",
            width: isMobile ? "220px" : "340px",
            height: isMobile ? "220px" : "340px",
            background: colors.glowPurple,
            filter: "blur(70px)",
            borderRadius: "999px",
            animation: "hookoraFloatTwo 12s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-80px",
            left: "18%",
            width: isMobile ? "220px" : "320px",
            height: isMobile ? "220px" : "320px",
            background: colors.glowGreen,
            filter: "blur(72px)",
            borderRadius: "999px",
            animation: "hookoraFloatThree 11s ease-in-out infinite",
          }}
        />
      </div>

      <Navbar />

      <div
        style={{
          maxWidth: "560px",
          margin: isMobile ? "24px auto" : "48px auto",
          padding: isMobile ? "14px" : "20px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            position: "relative",
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: isMobile ? "20px" : "28px",
            padding: isMobile ? "22px" : "32px",
            boxShadow: `${colors.shadow}, ${colors.cardGlow}`,
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            opacity: mounted ? 1 : 0,
            transform: mounted
              ? "translateY(0px) scale(1)"
              : "translateY(18px) scale(0.985)",
            transition: "opacity 650ms ease, transform 650ms ease",
          }}
        >
          <div style={staggerStyle(60)}>
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "999px",
                  background: colors.badgeBg,
                  color: colors.badgeText,
                  fontSize: "12px",
                  fontWeight: "700",
                  marginBottom: "14px",
                  border: isDark ? `1px solid ${colors.border}` : "none",
                  animation: "hookoraPulseGlow 3.4s ease-in-out infinite",
                }}
              >
                {tt("registerPage.badge", "✨ Create your account")}
              </div>

              <h1
                style={{
                  marginTop: 0,
                  marginBottom: "10px",
                  color: colors.text,
                  fontSize: isMobile ? "30px" : "36px",
                  lineHeight: "1.06"
                }}
              >
                {tt("registerPage.title", "Create your Hookora account")}
              </h1>

              <p
                style={{
                  color: colors.textMuted,
                  marginBottom: 0,
                  lineHeight: "1.7",
                  fontSize: "15px"
                }}
              >
                {tt(
                  "registerPage.subtitle",
                  "Start generating, saving, and managing hooks in one place."
                )}
              </p>
            </div>
          </div>

          <div style={staggerStyle(120)}>
            <div
              style={{
                marginBottom: "18px",
                background: colors.infoBg,
                border: `1px solid ${colors.infoBorder}`,
                color: colors.infoText,
                padding: "12px 14px",
                borderRadius: "14px",
                lineHeight: "1.6",
                fontSize: "14px",
              }}
              dangerouslySetInnerHTML={{
                __html: tt(
                  "registerPage.referralBanner",
                  "Use a referral code to help someone earn <strong>Reward Credits</strong>. Referrers earn ₦300 after signup plus email verification, and an extra ₦700 after a paid upgrade."
                ),
              }}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ ...staggerStyle(180), marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textSoft,
                  fontWeight: "600",
                  fontSize: "14px"
                }}
              >
                {tt("registerPage.fields.username.label", "Username")}
              </label>
              <input
                type="text"
                name="username"
                placeholder={tt(
                  "registerPage.fields.username.placeholder",
                  "Choose a username"
                )}
                value={formData.username}
                onChange={handleChange}
                onFocus={() => setActiveField("username")}
                onBlur={() => setActiveField("")}
                style={inputStyle("username")}
              />
            </div>

            <div style={{ ...staggerStyle(240), marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textSoft,
                  fontWeight: "600",
                  fontSize: "14px"
                }}
              >
                {tt("registerPage.fields.email.label", "Email")}
              </label>
              <input
                type="email"
                name="email"
                placeholder={tt(
                  "registerPage.fields.email.placeholder",
                  "Enter your email"
                )}
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setActiveField("email")}
                onBlur={() => setActiveField("")}
                style={inputStyle("email")}
              />
            </div>

            <div style={{ ...staggerStyle(300), marginBottom: "14px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textSoft,
                  fontWeight: "600",
                  fontSize: "14px"
                }}
              >
                {tt("registerPage.fields.password.label", "Password")}
              </label>

              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={tt(
                    "registerPage.fields.password.placeholder",
                    "Create a password"
                  )}
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setActiveField("password")}
                  onBlur={() => setActiveField("")}
                  style={{
                    ...inputStyle("password"),
                    marginBottom: 0,
                    flex: 1,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  style={{
                    minWidth: "54px",
                    height: "52px",
                    borderRadius: "14px",
                    border: `1px solid ${colors.borderStrong}`,
                    background: colors.eyeButtonBg,
                    color: colors.eyeButtonText,
                    cursor: "pointer",
                    fontSize: "18px",
                    fontWeight: "700",
                    transition: "transform 160ms ease, opacity 160ms ease",
                    transform: showPassword ? "scale(1.04)" : "scale(1)",
                  }}
                  aria-label={
                    showPassword
                      ? tt("registerPage.aria.hidePassword", "Hide password")
                      : tt("registerPage.aria.showPassword", "Show password")
                  }
                  title={
                    showPassword
                      ? tt("registerPage.aria.hidePassword", "Hide password")
                      : tt("registerPage.aria.showPassword", "Show password")
                  }
                >
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>

              <div
                style={{
                  marginTop: "10px",
                  background: colors.hintBg,
                  border: `1px solid ${colors.hintBorder}`,
                  borderRadius: "14px",
                  padding: "12px",
                  display: "grid",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    color: colors.textSoft,
                    fontSize: "13px",
                    fontWeight: "700",
                    marginBottom: "2px",
                  }}
                >
                  {tt(
                    "registerPage.passwordHints.title",
                    "Password should include:"
                  )}
                </div>
                {renderCheck(
                  passwordChecks.minLength,
                  tt(
                    "registerPage.passwordHints.minLength",
                    "At least 6 characters"
                  )
                )}
                {renderCheck(
                  passwordChecks.hasLetter,
                  tt("registerPage.passwordHints.hasLetter", "At least 1 letter")
                )}
                {renderCheck(
                  passwordChecks.hasNumberOrSymbol,
                  tt(
                    "registerPage.passwordHints.hasNumberOrSymbol",
                    "At least 1 number or symbol"
                  )
                )}
              </div>
            </div>

            <div style={{ ...staggerStyle(360), marginBottom: "14px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textSoft,
                  fontWeight: "600",
                  fontSize: "14px"
                }}
              >
                {tt("registerPage.fields.referralCode.label", "Referral Code")}{" "}
                <span style={{ color: colors.textMuted, fontWeight: "500" }}>
                  {tt("registerPage.fields.referralCode.optional", "(optional)")}
                </span>
              </label>
              <input
                type="text"
                name="referral_code"
                placeholder={tt(
                  "registerPage.fields.referralCode.placeholder",
                  "Enter referral code"
                )}
                value={formData.referral_code}
                onChange={handleChange}
                onFocus={() => setActiveField("referral_code")}
                onBlur={() => setActiveField("")}
                style={{
                  ...inputStyle("referral_code"),
                  letterSpacing: formData.referral_code ? "0.04em" : "normal",
                  fontWeight: formData.referral_code ? "700" : "400",
                }}
              />
              <div
                style={{
                  marginTop: "8px",
                  color: colors.textMuted,
                  fontSize: "13px",
                  lineHeight: "1.5"
                }}
              >
                {tt(
                  "registerPage.fields.referralCode.help",
                  "Signing up through a referral helps your referrer earn Reward Credits after your email is verified."
                )}
              </div>
            </div>

            <div style={staggerStyle(420)}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  marginTop: "6px",
                  padding: "14px",
                  background: loading
                    ? "#9ca3af"
                    : "linear-gradient(135deg, #16a34a, #059669)",
                  color: "white",
                  border: "none",
                  borderRadius: "14px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontWeight: "700",
                  fontSize: "15px",
                  boxShadow: colors.buttonShadow,
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease",
                }}
              >
                {!loading && (
                  <span
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "40%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)",
                      transform: "translateX(-120%)",
                      animation: "hookoraButtonShimmer 3.6s linear infinite",
                    }}
                  />
                )}
                <span style={{ position: "relative", zIndex: 1 }}>
                  {loading
                    ? tt("registerPage.actions.creatingAccount", "Creating account...")
                    : tt("registerPage.actions.register", "Register")}
                </span>
              </button>
            </div>
          </form>

          {error && (
            <div
              style={{
                ...staggerStyle(460),
                marginTop: "16px",
                color: colors.errorText,
                background: colors.errorBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.errorBorder}`,
                lineHeight: "1.5"
              }}
            >
              {error}
            </div>
          )}

          {success && (
            <div
              style={{
                ...staggerStyle(500),
                marginTop: "16px",
                color: colors.successText,
                background: colors.successBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.successBorder}`,
                lineHeight: "1.6"
              }}
            >
              <div style={{ fontWeight: "700", marginBottom: "6px" }}>
                {success}
              </div>
              <div style={{ fontSize: "14px" }}>
                {tt(
                  "registerPage.messages.verifyInbox",
                  "Check your inbox for a verification email. After verifying, you can log in and your referral eligibility will be counted correctly."
                )}
              </div>
            </div>
          )}

          {infoMessage && (
            <div
              style={{
                ...staggerStyle(540),
                marginTop: "16px",
                color: colors.infoText,
                background: colors.infoBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.infoBorder}`,
                lineHeight: "1.5"
              }}
            >
              {infoMessage}
            </div>
          )}

          {referralWarning && (
            <div
              style={{
                ...staggerStyle(580),
                marginTop: "16px",
                color: colors.warningText,
                background: colors.warningBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.warningBorder}`,
                lineHeight: "1.5"
              }}
            >
              {tt("registerPage.messages.referralNote", "Referral note:")}{" "}
              {referralWarning}
            </div>
          )}

          {(success || registeredEmail) && (
            <div
              style={{
                ...staggerStyle(620),
                marginTop: "16px",
                display: "flex",
                gap: "10px",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                style={{
                  flex: 1,
                  padding: "12px 14px",
                  borderRadius: "12px",
                  border: "none",
                  background: colors.secondaryButtonBg,
                  color: colors.secondaryButtonText,
                  fontWeight: "700",
                  cursor: resendLoading ? "not-allowed" : "pointer",
                  opacity: resendLoading ? 0.8 : 1,
                  transition: "transform 160ms ease",
                }}
              >
                {resendLoading
                  ? tt("registerPage.actions.sending", "Sending...")
                  : tt(
                      "registerPage.actions.resendVerificationEmail",
                      "Resend Verification Email"
                    )}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                style={{
                  flex: 1,
                  padding: "12px 14px",
                  borderRadius: "12px",
                  border: "none",
                  background: "#2563eb",
                  color: "#ffffff",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "transform 160ms ease",
                }}
              >
                {tt("registerPage.actions.goToLogin", "Go to Login")}
              </button>
            </div>
          )}

          <div
            style={{
              ...staggerStyle(680),
              marginTop: "20px",
              paddingTop: "18px",
              borderTop: `1px solid ${colors.divider}`
            }}
          >
            <p style={{ margin: 0, color: colors.textMuted, lineHeight: "1.6", fontSize: "14px" }}>
              {tt("registerPage.links.alreadyHaveAccount", "Already have an account?")}{" "}
              <Link
                to="/login"
                style={{
                  color: colors.accentLink,
                  fontWeight: "700",
                  textDecoration: "none"
                }}
              >
                {tt("registerPage.actions.login", "Login")}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default RegisterPage;